<template>
  <div class="card p-6 mb-6">
    <h3 class="text-base font-semibold mb-1">{{ $t('AI_SETTINGS.DATA_SYNC.TITLE') }}</h3>
    <p class="text-slate-600 mb-4">{{ $t('AI_SETTINGS.DATA_SYNC.DESCRIPTION') }}</p>

    <div class="grid grid-cols-1 gap-4">
      <!-- Help Section -->
      <div>
        <button class="text-indigo-600 text-sm underline" @click="showHelp = !showHelp">
          {{ showHelp ? $t('AI_SETTINGS.DATA_SYNC.HIDE_HELP') : $t('AI_SETTINGS.DATA_SYNC.SHOW_HELP') }}
        </button>
        <div v-if="showHelp" class="mt-3 p-3 rounded border border-slate-200 bg-slate-50 text-sm text-slate-700">
          <ol class="list-decimal ml-5 space-y-1">
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_OPEN_SHEET')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_FIND_ID')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_SHEET_NAME')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_CREATE_SA')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_ENABLE_API')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_SHARE')"></li>
            <li v-html="$t('AI_SETTINGS.DATA_SYNC.HELP.STEP_TEST')"></li>
          </ol>
        </div>
      </div>

      <!-- Enable Sync Toggle -->
      <div class="flex items-center justify-between">
        <div>
          <label class="font-medium">{{ $t('AI_SETTINGS.DATA_SYNC.ENABLE') }}</label>
          <p class="text-xs text-slate-500">{{ $t('AI_SETTINGS.DATA_SYNC.ENABLE_HINT') }}</p>
        </div>
        <woot-switch 
          :model-value="form.is_enabled" 
          :disabled="isSaving"
          @input="onToggleSync" 
        />
      </div>

      <!-- Google Sheets Configuration (only shown when enabled) -->
      <div v-if="form.is_enabled" class="space-y-4 border-t pt-4">
        <div>
          <label class="form-label">{{ $t('AI_SETTINGS.DATA_SYNC.SPREADSHEET_ID') }}</label>
          <input v-model="form.google_spreadsheet_id" type="text" class="form-input" />
          <p class="text-xs text-slate-500 mt-1">{{ $t('AI_SETTINGS.DATA_SYNC.SPREADSHEET_ID_HINT') }}</p>
        </div>

        <div>
          <label class="form-label">{{ $t('AI_SETTINGS.DATA_SYNC.SHEET_NAME') }}</label>
          <input v-model="form.google_sheet_name" type="text" class="form-input" />
          <p class="text-xs text-slate-500 mt-1">{{ $t('AI_SETTINGS.DATA_SYNC.SHEET_NAME_HINT') }}</p>
        </div>

        <div>
          <label class="form-label">{{ $t('AI_SETTINGS.DATA_SYNC.SERVICE_ACCOUNT_JSON') }}</label>
          <textarea v-model="form.google_credentials" rows="6" class="form-input font-mono" :placeholder="$t('AI_SETTINGS.DATA_SYNC.SERVICE_ACCOUNT_PLACEHOLDER')" />
          <p class="text-slate-500 text-xs mt-1">{{ $t('AI_SETTINGS.DATA_SYNC.SERVICE_ACCOUNT_HINT') }}</p>
          <p class="text-slate-500 text-xs mt-1">{{ $t('AI_SETTINGS.DATA_SYNC.SHARE_HINT') }}</p>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center gap-3">
          <woot-button :is-loading="isTesting" :disabled="!canTest" appearance="secondary" @click="onTest">
            {{ $t('AI_SETTINGS.DATA_SYNC.TEST_CONNECTION') }}
          </woot-button>
          <woot-button :is-loading="isSaving" :disabled="!canSave" @click="onSave">
            {{ $t('AI_SETTINGS.DATA_SYNC.SAVE') }}
          </woot-button>
        </div>

        <!-- Last Sync Info -->
        <div v-if="config.last_synced_at" class="text-xs text-slate-600">
          {{ $t('AI_SETTINGS.DATA_SYNC.LAST_SYNCED_AT') }}: {{ humanize(config.last_synced_at) }}
        </div>
      </div>

      <!-- Status Display -->
      <div v-if="form.is_enabled" class="border-t pt-4">
        <woot-label :title="$t('AI_SETTINGS.DATA_SYNC.STATUS.ENABLED')" :description="''" />
      </div>
      <div v-else class="border-t pt-4">
        <woot-label :title="$t('AI_SETTINGS.DATA_SYNC.STATUS.DISABLED')" :description="''" />
      </div>
    </div>
  </div>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import DataSyncConfigAPI from 'dashboard/api/dataSyncConfig';

export default {
  name: 'GoogleSheetsSyncCard',
  data() {
    return {
      isLoading: false,
      isTesting: false,
      isSaving: false,
      showHelp: false,
      config: {},
      form: {
        is_enabled: false,
        google_spreadsheet_id: '',
        google_sheet_name: '',
        google_credentials: '',
      },
    };
  },
  computed: {
    canTest() {
      if (!this.form.is_enabled) return false;
      return (
        Boolean(this.form.google_spreadsheet_id) &&
        Boolean(this.form.google_sheet_name) &&
        Boolean((this.form.google_credentials || '').trim())
      );
    },
    canSave() {
      // When disabled, always allow save
      if (!this.form.is_enabled) return true;
      // When enabled, require all Google Sheets fields
      return (
        Boolean(this.form.google_spreadsheet_id) &&
        Boolean(this.form.google_sheet_name) &&
        Boolean((this.form.google_credentials || '').trim())
      );
    },
  },
  mounted() {
    this.fetchConfig();
  },
  methods: {
    async fetchConfig() {
      this.isLoading = true;
      try {
        const { data } = await DataSyncConfigAPI.getConfig();
        this.config = data;
        this.form.is_enabled = data.is_enabled;
        this.form.google_spreadsheet_id = data.google_spreadsheet_id || '';
        this.form.google_sheet_name = data.google_sheet_name || '';
      } catch (e) {
        useAlert(this.$t('GENERAL_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    async onTest() {
      if (!this.canTest) return;
      this.isSaving = true;
      try {
        await DataSyncConfigAPI.updateConfig(this.buildPayload(true));
      } catch (e) { console.warn('Pre-save for test failed', e); }
      this.isSaving = false;

      this.isTesting = true;
      try {
        const { data } = await DataSyncConfigAPI.testConnection();
        if (data.success) {
          useAlert(this.$t('AI_SETTINGS.DATA_SYNC.CONNECTION_SUCCESS'));
        } else {
          useAlert(data.message || this.$t('GENERAL_ERROR'));
        }
      } catch (e) {
        useAlert(e?.response?.data?.message || this.$t('GENERAL_ERROR'));
      } finally {
        this.isTesting = false;
      }
    },
    async onSave() {
      if (!this.canSave) return;
      this.isSaving = true;
      try {
        await DataSyncConfigAPI.updateConfig(this.buildPayload(true));
        useAlert(this.$t('AI_SETTINGS.DATA_SYNC.SAVE_SUCCESS'), 'success');
        this.form.google_credentials = '';
        await this.fetchConfig();
      } catch (e) {
        useAlert(e?.response?.data?.message || this.$t('GENERAL_ERROR'));
      } finally {
        this.isSaving = false;
      }
    },
    async onToggleSync(value) {
      // If turning ON: just reveal form, do not persist yet
      if (value) {
        this.form.is_enabled = true;
        return;
      }

      // If turning OFF: persist immediately
      this.form.is_enabled = false;
      this.isSaving = true;
      try {
        await DataSyncConfigAPI.updateConfig(this.buildPayload());
        useAlert(this.$t('AI_SETTINGS.DATA_SYNC.SAVE_SUCCESS'), 'success');
        await this.fetchConfig();
      } catch (e) {
        // Revert the toggle if save failed
        this.form.is_enabled = true;
        useAlert(e?.response?.data?.message || this.$t('GENERAL_ERROR'));
      } finally {
        this.isSaving = false;
      }
    },
    buildPayload(includeCredentials = false) {
      const payload = {
        is_enabled: this.form.is_enabled,
        target: 'google_sheets', // Always Google Sheets
        google_spreadsheet_id: this.form.google_spreadsheet_id,
        google_sheet_name: this.form.google_sheet_name,
      };
      if (includeCredentials && this.form.google_credentials) {
        payload.google_credentials = this.form.google_credentials;
      }
      return payload;
    },
    isJson(text) {
      try {
        JSON.parse(text);
        return true;
      } catch (_) {
        return false;
      }
    },
    humanize(iso) {
      try {
        return new Date(iso).toLocaleString();
      } catch (_) {
        return iso;
      }
    },
  },
};
</script>

<style scoped>
.card { 
  background-color: var(--color-background); 
  border: 1px solid var(--color-border); 
  border-radius: 0.5rem; 
}
.form-label { 
  display: block; 
  font-size: 0.875rem; 
  font-weight: 500; 
  margin-bottom: 0.25rem; 
}
.form-input { 
  width: 100%; 
  border: 1px solid var(--color-border); 
  border-radius: 0.25rem; 
  padding: 0.5rem 0.75rem; 
  background-color: var(--color-background);
  color: var(--color-body);
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.form-input:focus {
  outline: none;
  border-color: var(--color-woot-500);
  box-shadow: 0 0 0 2px var(--color-woot-100);
}
.form-input::placeholder {
  color: var(--color-body-light);
}
</style>


